import { queryOptions, useQuery, useSuspenseQuery } from '@tanstack/react-query';

import type { QueryConfig } from '@/lib/react-query';
import type { LoaderProps } from '@/lib/router';
import { api, extractData } from '@/lib/treaty';

export type Estate = NonNullable<
  Awaited<ReturnType<ReturnType<typeof api.estates>['all']['get']>>['data']
>;

type EstateQueryOptions = {
  queryConfig?: QueryConfig<typeof getEstateQueryOptions>;
  id: Estate['id'];
};

type EstateSuspenseQueryOptions = {
  id: Estate['id'];
};

type EstateBaseQueryOptions = {
  queryConfig?: QueryConfig<typeof getEstateBaseQueryOptions>;
  id: Estate['id'];
};

export const getEstate = (id: Estate['id']) => {
  return extractData(api.estates({ id: id }).all.get());
};

export const getEstateOptions = (id: Estate['id']) => ({
  queryKey: ['estates', id],
  queryFn: () => getEstate(id),
});

export const getEstateQueryOptions = (id: Estate['id']) => {
  return queryOptions(getEstateOptions(id));
};

export const ensureEstateQueryData = ({ context: { queryClient }, params }: LoaderProps) => {
  return queryClient.ensureQueryData(getEstateQueryOptions(params.estateId));
};

export const useEstate = ({ id }: EstateSuspenseQueryOptions) => {
  return useSuspenseQuery(getEstateQueryOptions(id));
};

export const useEstateQuery = ({ queryConfig, id }: EstateQueryOptions) => {
  return useQuery({ ...getEstateQueryOptions(id), ...queryConfig });
};

export const getEstateBaseInfo = (id: Estate['id']) => {
  return extractData(api.estates({ id }).base.get());
};

export const getEstateBaseOptions = (id: Estate['id']) => ({
  queryKey: ['estates', id, 'base'],
  queryFn: () => getEstateBaseInfo(id),
});

export const getEstateBaseQueryOptions = (id: Estate['id']) => {
  return queryOptions(getEstateBaseOptions(id));
};

export const ensureEstateBaseQueryData = ({ context: { queryClient }, params }: LoaderProps) => {
  return queryClient.ensureQueryData(getEstateBaseQueryOptions(params.estateId));
};

export const useEstateBaseInfo = ({ queryConfig, id }: EstateBaseQueryOptions) => {
  return useSuspenseQuery({ ...queryConfig, ...getEstateBaseQueryOptions(id) });
};
